"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  UtensilsCrossed,
  Info,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  GUNLUK_ISTISNA_NAKIT,
  GUNLUK_ISTISNA_KART_KDV_DAHIL,
  YILI,
  hesaplaYemekUcreti,
  formatTL,
  type YemekUcretiSonuc,
  type OdemeTipi,
} from "./yemek-ucreti-engine";

const howToSchema = generateHowToSchema({
  title: "Yemek Ücreti Nasıl Hesaplanır?",
  description:
    "Günlük yemek tutarını girerek aylık toplam, istisna tutar ve kesintileri hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Ödeme Tipini Seçin",
      text: "Nakit veya yemek kartı olarak seçin.",
    },
    {
      name: "Günlük Tutarı Girin",
      text: "Günlük yemek ücretini TL olarak girin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "İstisna tutar, vergiye tabi kısım ve kesintileri görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

export default function YemekUcretiHesaplayiciClient() {
  const [odemeTipi, setOdemeTipi] = useState<OdemeTipi>("kart");
  const [gunlukTutar, setGunlukTutar] = useState("");
  const [calisilanGun, setCalisilanGun] = useState("22");
  const [sonuc, setSonuc] = useState<YemekUcretiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const tutar = parseFloat(gunlukTutar);
    const gun = parseInt(calisilanGun) || 22;
    if (!gunlukTutar || isNaN(tutar) || tutar <= 0) {
      setHata("Lütfen geçerli bir günlük yemek tutarı giriniz.");
      setSonuc(null);
      return;
    }
    setSonuc(
      hesaplaYemekUcreti({
        odemeTipi,
        gunlukTutar: tutar,
        aylikCalisilanGun: gun,
      })
    );
  }, [odemeTipi, gunlukTutar, calisilanGun]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  const gunlukIstisna =
    odemeTipi === "kart" ? GUNLUK_ISTISNA_KART_KDV_DAHIL : GUNLUK_ISTISNA_NAKIT;

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-br from-rose-500 to-pink-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Yemek Ücreti Hesaplayıcı",
                  href: "/araclar/yemek-ucreti-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <UtensilsCrossed className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Yemek Ücreti Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} İstisna Tutarları
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Nakit veya kart yemek yardımının vergiye tabi kısmını ve
              kesintilerini hesaplayın. {YILI} güncel istisna tutarları dahil.
            </p>
          </div>
        </section>

        {/* İstisna Bilgileri */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {YILI} Günlük Yemek Ücreti İstisna Tutarları
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div
                className={`rounded-xl p-5 cursor-pointer transition-all ${
                  odemeTipi === "kart"
                    ? "bg-rose-500 text-white ring-2 ring-rose-400"
                    : "bg-white border border-gray-200 hover:border-rose-300"
                }`}
                onClick={() => setOdemeTipi("kart")}
              >
                <span
                  className={`text-xs block mb-1 ${odemeTipi === "kart" ? "text-rose-100" : "text-gray-500"}`}
                >
                  Yemek Kartı / Ayni
                </span>
                <span
                  className={`text-2xl font-bold block ${odemeTipi === "kart" ? "text-white" : "text-rose-600"}`}
                >
                  {formatTL(GUNLUK_ISTISNA_KART_KDV_DAHIL)}
                </span>
                <span
                  className={`text-xs block mt-1 ${odemeTipi === "kart" ? "text-rose-200" : "text-gray-400"}`}
                >
                  KDV Dahil / gün
                </span>
                {odemeTipi === "kart" && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-rose-100">
                    <CheckCircle className="w-3 h-3" />
                    SGK&apos;dan muaf
                  </div>
                )}
              </div>
              <div
                className={`rounded-xl p-5 cursor-pointer transition-all ${
                  odemeTipi === "nakit"
                    ? "bg-rose-500 text-white ring-2 ring-rose-400"
                    : "bg-white border border-gray-200 hover:border-rose-300"
                }`}
                onClick={() => setOdemeTipi("nakit")}
              >
                <span
                  className={`text-xs block mb-1 ${odemeTipi === "nakit" ? "text-rose-100" : "text-gray-500"}`}
                >
                  Nakit Ödeme
                </span>
                <span
                  className={`text-2xl font-bold block ${odemeTipi === "nakit" ? "text-white" : "text-rose-600"}`}
                >
                  {formatTL(GUNLUK_ISTISNA_NAKIT)}
                </span>
                <span
                  className={`text-xs block mt-1 ${odemeTipi === "nakit" ? "text-rose-200" : "text-gray-400"}`}
                >
                  / gün
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="gunlukTutar"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Günlük Yemek Tutarı
                  </label>
                  <div className="relative">
                    <input
                      id="gunlukTutar"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={gunlukTutar}
                      onChange={(e) => setGunlukTutar(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGunlukTutar(gunlukIstisna.toString())}
                    className="mt-2 text-xs text-rose-600 hover:text-rose-700 font-medium"
                  >
                    İstisna Tutarı ({formatTL(gunlukIstisna)})
                  </button>
                </div>
                <div>
                  <label
                    htmlFor="calisilanGun"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Aylık Çalışılan Gün
                  </label>
                  <input
                    id="calisilanGun"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="31"
                    value={calisilanGun}
                    onChange={(e) => setCalisilanGun(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-gray-900"
                  />
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-rose-500 text-white py-4 rounded-xl font-semibold hover:bg-rose-600 active:bg-rose-700 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {sonuc.tamIstisnaMi && (
                  <div className="mb-4 flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-700">
                      <strong>Tam İstisna:</strong> Günlük tutarınız istisna
                      limitinin altında olduğu için herhangi bir kesinti
                      uygulanmaz.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-rose-500 text-white rounded-xl p-4">
                    <span className="text-xs text-rose-100 block">
                      Aylık Toplam
                    </span>
                    <span className="text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(sonuc.aylikToplamYemek)}
                    </span>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <span className="text-xs text-emerald-600 block">
                      İstisna Tutar
                    </span>
                    <span className="text-lg font-bold text-emerald-700 mt-1 block tabular-nums">
                      {formatTL(sonuc.aylikIstisnaTutar)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Vergiye Tabi
                    </span>
                    <span className="text-lg font-bold text-red-500 mt-1 block tabular-nums">
                      {formatTL(sonuc.aylikVergiyeTabiTutar)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Net Yemek Ücreti
                    </span>
                    <span className="text-lg font-bold text-gray-900 mt-1 block tabular-nums">
                      {formatTL(sonuc.aylikNetYemekUcreti)}
                    </span>
                  </div>
                </div>

                {/* Kesinti Detayları */}
                {!sonuc.tamIstisnaMi && (
                  <div className="bg-white rounded-xl p-5 border border-gray-200 mb-4">
                    <h3 className="font-bold text-gray-900 mb-3">
                      Kesinti Detayları
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Günlük tutar</span>
                        <span className="font-medium tabular-nums">
                          {formatTL(sonuc.gunlukTutar)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Günlük istisna</span>
                        <span className="font-medium text-emerald-600 tabular-nums">
                          -{formatTL(sonuc.gunlukIstisna)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Günlük vergiye tabi
                        </span>
                        <span className="font-medium text-red-500 tabular-nums">
                          {formatTL(sonuc.gunlukVergiyeTabi)}
                        </span>
                      </div>
                      <div className="border-t pt-2 mt-2">
                        {sonuc.aylikSgkKesinti > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              SGK Kesintisi (%14)
                            </span>
                            <span className="font-medium text-red-500 tabular-nums">
                              -{formatTL(sonuc.aylikSgkKesinti)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Gelir Vergisi (%15)
                          </span>
                          <span className="font-medium text-red-500 tabular-nums">
                            -{formatTL(sonuc.aylikGelirVergisi)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Damga Vergisi (%0,759)
                          </span>
                          <span className="font-medium text-red-500 tabular-nums">
                            -{formatTL(sonuc.aylikDamgaVergisi)}
                          </span>
                        </div>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span className="text-gray-900">Toplam Kesinti</span>
                        <span className="text-red-600 tabular-nums">
                          -{formatTL(sonuc.aylikToplamKesinti)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Yıllık Karşılaştırma */}
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    Yıllık Karşılaştırma
                  </h3>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <span className="text-xs text-gray-500 block">
                        Yıllık Toplam
                      </span>
                      <span className="font-bold text-sm tabular-nums">
                        {formatTL(sonuc.yillikToplamYemek)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">
                        Yıllık İstisna
                      </span>
                      <span className="font-bold text-sm text-emerald-600 tabular-nums">
                        {formatTL(sonuc.yillikIstisnaTutar)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">
                        Yıllık Vergiye Tabi
                      </span>
                      <span className="font-bold text-sm text-red-500 tabular-nums">
                        {formatTL(sonuc.yillikVergiyeTabiTutar)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            <SSSSoru
              soru="Yemek kartıyla nakit arasındaki fark nedir?"
              cevap="Yemek kartı ile sağlanan yemek bedelleri SGK'dan muaftır. Nakit ödemede ise istisna aşan kısım hem SGK hem vergi kesintisine tabidir."
            />
            <SSSSoru
              soru="İstisna tutarını aşmayan yemek ödemesinde kesinti var mı?"
              cevap={`Hayır. Günlük ${odemeTipi === "kart" ? formatTL(GUNLUK_ISTISNA_KART_KDV_DAHIL) : formatTL(GUNLUK_ISTISNA_NAKIT)} istisna tutarını aşmayan yemek ödemelerinden herhangi bir kesinti yapılmaz.`}
            />
            <SSSSoru
              soru="Yemek ücreti bordrolara nasıl yansır?"
              cevap="İstisna tutarını aşan kısım, brüt ücrete eklenerek vergilendirilir. İstisna kapsamındaki tutar bordroda ayrı kalem olarak gösterilir."
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                {YILI} yılı günlük istisna tutarları kullanılmıştır. Gelir
                vergisi oranı basitleştirilmiş olarak %15 uygulanmıştır.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              Sonuc Yorumu ve Aksiyon Onerisi
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Yemek bedeli kararlarini ucret paketi ve toplam istihdam maliyeti
              ile birlikte ele almak, bordroda surpriz maliyetlerin onune gecer.
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link
                href="/yazilim/ucret-ve-yan-hak-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Yan hak yonetimi
              </Link>
              <Link
                href="/araclar/isveren-maliyeti-hesaplayici"
                className="text-indigo-600 hover:underline"
              >
                Toplam maliyet hesabi
              </Link>
              <Link
                href="/cozumler/avans-finans"
                className="text-indigo-600 hover:underline"
              >
                Finansal IK surecleri
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Yan Hak Politika Notlari
            </h2>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Istisna limiti ve toplam maliyet birlikte okunmalidir
            </h3>
            <p className="text-sm text-gray-600">
              Yemek yardimi kararlarinda gunluk istisna, SGK etkisi ve butce
              hedefi birlikte takip edilerek daha dengeli bir paket yapisi
              kurulabilir.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="yemek-ucreti-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-rose-500 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Bordro Yönetimini Otomatikleştirin
            </h2>
            <p className="text-rose-100 mb-6 max-w-xl mx-auto">
              IKAI ile yemek ücreti, SGK ve tüm bordro süreçlerini tek
              platformda yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-rose-700 px-6 py-3 rounded-xl font-semibold hover:bg-rose-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
